import React, { useCallback, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { apiRequest } from '../../helpers'
import { notification } from 'antd'
import {
  loginStart,
  loginSuccess,
  loginFailure,
  logoutSuccess,
  updateUserData as updateUserDataAction,
  selectUser,
  selectIsAuthenticated,
} from '../../slices/authSlice'

//eslint-disable-next-line
const apiUrl = process.env.REACT_APP_API

export const AuthorizationContext = React.createContext({
  userData: null,
  login: () => {
    return
  },
  logout: () => {
    return
  },
})

const AuthorizationProvider = ({ children }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const userData = useSelector(selectUser)
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const refreshIntervalRef = useRef(null)

  const login = async x => {
    dispatch(loginStart())

    const res = await apiRequest(`${apiUrl}auth/login`, 'POST', x)
    if (!res?.ok) {
      dispatch(loginFailure('Invalid credentials'))
      notification.error({
        message: 'System Error!',
        description: 'Invalid credentials!',
      })
      return
    }

    // Store user profile in localStorage for persistence across page reloads
    localStorage.setItem('user_profile', JSON.stringify(res.data.profile))

    dispatch(loginSuccess(res.data.profile))

    // Start token refresh interval after successful login
    startTokenRefresh()

    navigate('/')
    return res
  }

  const updateUserData = async x => {
    // Update in localStorage too for persistence
    if (x) {
      localStorage.setItem('user_profile', JSON.stringify(x))
    }
    dispatch(updateUserDataAction(x))
  }

  // Function to refresh the JWT token
  const refreshToken = useCallback(async () => {
    try {
      const res = await apiRequest(`${apiUrl}auth/refresh`, 'POST')
      if (res?.ok && res?.data?.profile) {
        // Update user data with fresh token information
        localStorage.setItem('user_profile', JSON.stringify(res.data.profile))
        dispatch(loginSuccess(res.data.profile))
        return true
      }
      return false
    } catch (error) {
      console.error('Failed to refresh token:', error)
      return false
    }
  }, [dispatch])

  // Function to start the token refresh interval
  const startTokenRefresh = useCallback(() => {
    // Clear existing interval if any
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current)
    }

    // Refresh token every 20 hours (4 hours before expiration)
    // This ensures the token is refreshed before it expires
    refreshIntervalRef.current = setInterval(() => {
      if (isAuthenticated) {
        refreshToken()
      }
    }, 20 * 60 * 60 * 1000) // 20 hours in milliseconds
  }, [isAuthenticated, refreshToken])

  // Function to stop the token refresh interval
  const stopTokenRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current)
      refreshIntervalRef.current = null
    }
  }, [])

  /** Check if user is authenticated based on Redux state */
  const checkIfLoggedIn = useCallback(() => {
    return isAuthenticated
  }, [isAuthenticated])

  /** At app initialization, fetch current user data if cookie exists */
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // Skip profile check if we're on the login page to avoid infinite redirects
        if (window.location.pathname === '/login') {
          return
        }

        // First check if we have user data in localStorage
        const storedProfile = localStorage.getItem('user_profile')
        if (storedProfile) {
          const profileData = JSON.parse(storedProfile)
          // Ensure the profile data has all required fields
          if (profileData && profileData.permissions && profileData.theme && profileData.language) {
            dispatch(loginSuccess(profileData))
          }
        }

        // Then fetch fresh data from the server
        const res = await apiRequest(`${apiUrl}auth/profile`)
        if (res?.ok && res?.data) {
          // Store in localStorage for persistence across page reloads
          localStorage.setItem('user_profile', JSON.stringify(res.data))
          dispatch(loginSuccess(res.data))

          // Start token refresh interval if user is authenticated
          startTokenRefresh()
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error)
      }
    }

    // Only fetch if not already authenticated
    if (!isAuthenticated) {
      fetchUserData()
    }
  }, [dispatch, isAuthenticated, startTokenRefresh])

  /** Makes a GET request to the logout endpoint to delete the 'access_token' cookie and redirect to login */
  const logout = async () => {
    const res = await apiRequest(`${apiUrl}auth/logout`)

    // Stop token refresh interval
    stopTokenRefresh()

    // Clear user profile from localStorage
    localStorage.removeItem('user_profile')

    dispatch(logoutSuccess())

    if (!res?.ok) return
    navigate('/')
  }

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      stopTokenRefresh()
    }
  }, [stopTokenRefresh])

  const contextValue = {
    updateUserData,
    userData,
    checkIfLoggedIn,
    login,
    logout,
  }

  return <AuthorizationContext.Provider value={contextValue}>{children}</AuthorizationContext.Provider>
}

export default AuthorizationProvider
